import dayjs from "dayjs";
import posthog from "posthog-js";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import { CalendarIcon, Circle, DollarSign, MapPinIcon, Trash } from "../../../assets/icons/interface/interface";
import SaveButton from "../../../components/SaveButton";
import { cn } from "../../../handlers/cn.utils";
import { getJobDeadline, isClosedJob } from "../../../handlers/jobStatus";
import { slugifyURL } from "../../../handlers/urlFunctions";
import { currencyFormat } from "../../../handlers/utilityFunctions";
import {
	prefetchCompany,
	prefetchCompanyJobs,
	prefetchJob,
	useDeleteJobMutation,
	useRecommendedJobs,
	useSavedJobs,
} from "../../../services/create-resume/queries";
import { ResumeDataService } from "../../../services/create-resume/resume-data";

export interface JobListingItemJob {
	_id?: string;
	jobTitle?: string;
	category?: string;
	companyName?: string;
	logo?: string;
	closeAt?: string;
	isPromoted?: boolean;
	matchScore?: number;
	company?: {
		_id?: string;
		company_name?: string;
		logo_url?: string;
	};
	jobLocation?: {
		city?: string;
		country?: string;
	};
	deadlineDate?: {
		year?: number;
		month?: number;
		day?: number;
	};
	salary?: {
		minPay?: number;
		maxPay?: number;
		payCurrency?: string;
		payFrequency?: string;
		payOption?: string;
	};
}

export interface JobListingItemProps {
	job: JobListingItemJob;
	onApply?: () => void;
	onClick?: () => void;
	deleteButton?: boolean;
	showCompany?: boolean;
	titleClassName?: string;
	hideSaveButton?: boolean;
	hideSalary?: boolean;
	showMatchScore?: boolean;
	hideDate?: boolean;
	hideCategory?: boolean;
	/** Overrides the Explore/View label, for lists that can only open a job for reading. */
	ctaLabel?: string;
	/** "secondary" de-emphasises the CTA on rows that are not an active opportunity. */
	ctaVariant?: "primary" | "secondary";
	/** Status chip rendered in place of the built-in Expired chip, e.g. "Closed". */
	statusBadge?: string;
}

const CTA_VARIANT_CLASSES = {
	primary: "bg-primary-700 text-white hover:bg-primary-800",
	secondary: "bg-gray-100 text-gray-800 hover:bg-gray-200",
};

const JobListingItem = ({
	job,
	onApply,
	onClick,
	deleteButton = false,
	showCompany = false,
	titleClassName = "",
	hideSaveButton = false,
	hideSalary = false,
	showMatchScore = false,
	hideDate = false,
	hideCategory = false,
	ctaLabel,
	ctaVariant = "primary",
	statusBadge,
}: JobListingItemProps) => {
	const jobService = React.useMemo(() => new ResumeDataService(), []);
	const [submitting, setSubmitting] = React.useState(false);
	const { refetch: refetchRecommendedJobs } = useRecommendedJobs();
	const { data: savedJobs, refetch: refetchSavedJobs } = useSavedJobs();

	const isSaved = useMemo(() => {
		if (!savedJobs || !job) return false;
		return savedJobs.some((savedJob) => savedJob._id === job._id);
	}, [savedJobs, job]);

	// Shared with the open/closed split, so a row can never contradict the section it sits in.
	const expired = isClosedJob(job);
	const deadline = getJobDeadline(job);
	const ctaText = ctaLabel || (expired ? "View" : "Explore");

	const saveInteraction = async () => {
		setSubmitting(true);
		jobService.saveJobInteraction(
			job?._id,
			{ interactionType: "Saved" },
			() => {
				refetchRecommendedJobs();
				refetchSavedJobs();
				setSubmitting(false);
			},
			() => {
				setSubmitting(false);
			},
		);
	};

	const { mutate: deleteJobMutation, isPending: deleting } = useDeleteJobMutation(job?._id);
	const deleteJobInteraction = async () => {
		deleteJobMutation();
	};

	// Never list a job with a missing company — skip it entirely.
	if (!job?.company) return null;

	return (
		<div className="flex justify-between items-center py-4 border-gray-200 transition-colors duration-300 hover:bg-gray-50">
			<div className="flex items-center cursor-pointer">
				{job?.logo && (
					<Link
						to={job.company._id ? `/companies/${job.company._id}` : "#"}
						state={job?.company}
						onMouseEnter={() => {
							prefetchCompany(job.company?._id);
							prefetchCompanyJobs(job.company?._id);
						}}
					>
						<div className="w-12 h-12 rounded-lg mr-4 flex items-center justify-center overflow-hidden border border-gray-100">
							<img
								src={job?.logo}
								alt={`${job?.company?.company_name} logo`}
								width={48}
								height={48}
								className="w-full h-full object-contain"
								onError={(e) => {
									(e.target as HTMLImageElement).src = "/images/co-placeholder.png";
								}}
							/>
						</div>
					</Link>
				)}
				<Link
					to={
						"/jobs/" +
						slugifyURL({
							_id: job?._id,
							companyName: job?.company?.company_name || job?.companyName,
							jobTitle: job?.jobTitle,
						})
					}
					onMouseEnter={() => prefetchJob(job?._id)}
					onClick={() => {
						if (onClick) onClick();
						window.scrollTo(0, 0);
					}}
				>
					<div className="flex items-center mb-1">
						<p
							className={`${titleClassName || "text-base"} font-semibold mr-2 font-inter transition-colors duration-300 hover:text-primary-700 text-gray-900`}
						>
							{job?.jobTitle}
						</p>
						{!hideCategory && job?.category && (
							<span className="text-xs px-2 rounded bg-gray-100 text-gray-800 h-5 flex items-center justify-center mr-2">
								{job?.category}
							</span>
						)}
						{job?.isPromoted && (
							<span className="text-xs px-2 rounded bg-blue-100 text-blue-800 h-5 flex items-center justify-center">
								Promoted
							</span>
						)}
						{statusBadge ? (
							<span className="text-xs px-2 rounded bg-red-100 text-red-800 h-5 flex items-center justify-center mr-2">
								{statusBadge}
							</span>
						) : (
							expired && (
								<span className="text-xs px-2 rounded bg-red-100 text-red-800 h-5 flex items-center justify-center mr-2">
									Expired
								</span>
							)
						)}
					</div>
					<p className="text-[14px] text-gray-600 flex items-center flex-wrap">
						{showMatchScore && job?.matchScore && (
							<span
								className={cn("inline-flex items-center h-6 px-2.5 py-0.5 rounded text-xs font-medium border mr-3", {
									"bg-green-100 text-green-800 border-green-200": job?.matchScore >= 80,
									"bg-blue-100 text-blue-800 border-blue-200": job?.matchScore >= 60 && job?.matchScore < 80,
									"bg-yellow-100 text-yellow-800 border-yellow-200": job?.matchScore < 60,
								})}
							>
								{job?.matchScore}% Match
							</span>
						)}
						{showCompany && <span className="mr-3">{job?.company?.company_name}</span>}
						<span className="flex items-center mr-3">
							<MapPinIcon className="w-4 h-4 mr-1" />
							{job?.jobLocation?.city}, {job?.jobLocation?.country}
						</span>
						{/* Read through the shared helper: it falls back to closeAt and returns "" for a
						    posting with no usable deadline, which used to read .year off undefined here. */}
						{!hideDate && deadline && (
							<span className="flex items-center mr-3">
								<CalendarIcon className="w-4 h-4 mr-1" />
								{dayjs(deadline).format("MMM DD, YYYY")}
							</span>
						)}
						{!hideSalary && job?.salary && (
							<span className="flex items-center">
								<DollarSign className="w-4 h-4 mr-1" />
								{job?.salary?.minPay > 1 ? (
									<>
										{job?.salary?.payCurrency}{" "}
										{job.salary.payOption === "Exact Amount" ? (
											<>
												{currencyFormat(job?.salary?.minPay)} {job?.salary?.payFrequency}
											</>
										) : (
											<>
												{currencyFormat(job?.salary?.minPay)} - {job?.salary?.payCurrency}{" "}
												{currencyFormat(job?.salary?.maxPay)} {job?.salary?.payFrequency}
											</>
										)}
									</>
								) : (
									"Negotiable"
								)}
							</span>
						)}
					</p>
				</Link>
			</div>
			<div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
				{!hideSaveButton && <SaveButton disabled={submitting} onClick={saveInteraction} isSaved={isSaved} />}
				{onApply && (
					<button
						onMouseEnter={() => prefetchJob(job?._id)}
						onClick={() => {
							// Track explore click. `cta` records the label pressed, since closed
							// rows and custom hosts can override it.
							posthog.capture("job_list_explore_clicked", {
								jobId: job?._id,
								jobTitle: job?.jobTitle,
								companyName: job?.company?.company_name,
								companyId: job?.company?._id,
								cta: ctaText,
							});
							onApply();
						}}
						className={cn(
							// min-w rather than a fixed width: Explore and View still land at 90px,
							// but a longer label such as "Explain this role" can grow instead of wrapping.
							"inline-flex items-center justify-center px-4 h-9 rounded transition-colors duration-300 text-sm font-inter min-w-[90px] shrink-0 whitespace-nowrap group relative overflow-hidden",
							CTA_VARIANT_CLASSES[ctaVariant],
						)}
					>
						<span className="mr-1 group-hover:mr-3 transition-all duration-300">{ctaText}</span>
						<span className="absolute right-4 opacity-0 transition-all duration-300 transform translate-x-2 group-hover:opacity-100 group-hover:translate-x-0">
							›
						</span>
					</button>
				)}
				{deleteButton && (
					<button
						disabled={deleting}
						onClick={() => {
							deleteJobInteraction();
						}}
						className="text-gray-500 hover:text-red-600 hover:bg-red-50 hover:bg-accent hover:text-accent-foreground"
					>
						{deleting ? <Circle className="h-4 w-4" /> : <Trash className="h-4 w-4" />}
						<span className="sr-only">Remove from saved</span>
					</button>
				)}
			</div>
		</div>
	);
};

export default JobListingItem;
